import { useEffect, useMemo, useState } from 'react';
import { useNavigate, useParams } from 'react-router-dom';
import { ApiClientError, type ApiClientErrorCode, type Furniture } from '@pomoshnik/shared';
import { apiClient } from '@/lib/apiClient';
import { useMediaImageUrls } from '@/lib/useMediaImageUrls';
import { FurnitureCardView, type FurnitureCardState } from './FurnitureCardView';

/**
 * Container Экрана 2.5.8 (Карточка фурнитуры). Блок 5, ADR-0068, ADR-0005.
 *
 * Загружает товар через `apiClient.furniture.get(globalId)` (P-012, без прямого
 * fetch). Wiring:
 *   - «Найти в магазинах» → navigate `/furniture/:globalId/shops` (роут — E7).
 *   - «Переслать клиенту» → ForwardSheet (Presenter) + clipboard fallback здесь.
 *   - «Назад к поиску» (not-found/error) → `/furniture/search`.
 *
 * P-001 verbatim: react-router-dom v7 декодирует path-param, поэтому `params.globalId`
 * приходит уже verbatim Global_ID; apiClient encodeURIComponent-ит обратно при URL.
 */
export function FurnitureCard() {
  const navigate = useNavigate();
  const params = useParams<{ globalId: string }>();
  const globalId = params.globalId ?? '';

  const [state, setState] = useState<FurnitureCardState>('loading');
  const [furniture, setFurniture] = useState<Furniture | undefined>(undefined);
  const [error, setError] = useState<{ code: ApiClientErrorCode; message: string } | undefined>(
    undefined,
  );

  useEffect(() => {
    if (!globalId) {
      setState('not-found');
      return;
    }
    setState('loading');
    setError(undefined);
    let cancelled = false;
    apiClient.furniture.get(globalId).then(
      (f) => {
        if (cancelled) return;
        setFurniture(f);
        setState('success');
      },
      (err: unknown) => {
        if (cancelled) return;
        if (err instanceof ApiClientError && err.code === 'NOT_FOUND') {
          setState('not-found');
          return;
        }
        // Не leak-аем String(err) в UI (security): non-ApiClientError → пустой message.
        const apiErr =
          err instanceof ApiClientError
            ? { code: err.code, message: err.message }
            : { code: 'INTERNAL_ERROR' as ApiClientErrorCode, message: '' };
        setError(apiErr);
        setState('error');
      },
    );
    return () => {
      cancelled = true;
    };
  }, [globalId]);

  // Hero signed-URL (ключ = imageFilename; правило «первое фото-товара» зашито на импорте).
  const heroKeys = useMemo(() => [furniture?.imageFilename], [furniture?.imageFilename]);
  const { urls: imageUrls } = useMediaImageUrls(heroKeys);
  const heroImageUrl = furniture?.imageFilename
    ? (imageUrls[furniture.imageFilename] ?? null)
    : null;

  /**
   * «Скопировать сообщение»: единый текст пересылки в буфер обмена, alert-уведомление
   * (Phase 8 заменит на toast). Зеркало decor-карточки.
   */
  async function onCopyMessage(text: string) {
    if (navigator.clipboard?.writeText) {
      try {
        await navigator.clipboard.writeText(text);
        alert('Сообщение скопировано');
        return;
      } catch {
        // clipboard недоступен (insecure context / permission) → fallback ниже
      }
    }
    alert(text);
  }

  function onFindShops() {
    navigate(`/furniture/${encodeURIComponent(globalId)}/shops`);
  }

  function onBackToSearch() {
    navigate('/furniture/search');
  }

  return (
    <FurnitureCardView
      state={state}
      furniture={furniture}
      error={error}
      heroImageUrl={heroImageUrl}
      onCopyMessage={onCopyMessage}
      onFindShops={onFindShops}
      onBackToSearch={onBackToSearch}
    />
  );
}
